<template>
  <v-menu v-if="isProfileLoaded"
          data-locator="app-bar-profile-menu"
          :open-on-hover="!$vuetify.display.xs"
          location="bottom"
          transition="slide-y-transition"
  >
    <template v-slot:activator="{ props, value }">
      <app-button
        :class="{'--is-active': isInProfileRoute || isInSubscriptionRoute}"
        data-locator="profile-menu-button"
        type="icon"
        v-bind="props"
      >
        <v-avatar class="profile-menu-item__initials text--x-small text--extra-bold"
                  data-locator="user-initials"
                  :class="{'profile-menu-item__initials--active': isInProfileRoute || isInSubscriptionRoute}"
                  size="24"
        >
          {{ profile.initials }}
        </v-avatar>
      </app-button>
    </template>

    <v-list v-model:selected="selectedPath"
            density="compact"
            class="profile-menu-item__menu"
    >
      <v-list-subheader class="px-4 pt-4">
        <h3 data-locator="user-full-name">{{ profile.fullName }}</h3>
        <p class="text-body-1" data-locator="user-email">{{ profile.email }}</p>
      </v-list-subheader>

      <v-list-item v-for="(pathItem, index) in paths"
                   :value="index"
                   :key="index"
                   :to="pathItem.to"
                   :href="pathItem.href"
                   :target="pathItem.href ? '_blank' : null"
                   :data-locator=pathItem.locatorValue
                   :disabled="pathItem.value === 'divider'"
                   :style="pathItem.value === 'divider' ? { minHeight: '2px'} : {}"
                   @click="pathItem.click ? pathItem.click() : null"
      >
        <v-list-item-title>
          <div class="d-flex align-center column-gap-2">
            <v-badge v-if="isInProfileRoute && pathItem.icon === 'person'"
                     :dot="true"
                     :model-value="!canCurrentUserSign"
                     class="mt-1"
                     color="error"
                     :offset-x="-5"
                     :offset-y="-3"
            >
              <app-material-icon 
                :name="pathItem.icon"
                :filled="isInProfileRoute"
                :class="{'text-primary': currentRoute.name.includes(pathItem.to.name)}"
                size="sm"
              />
            </v-badge>
            <app-material-icon v-else-if="!pathItem.customRender"
                      :name="pathItem.icon"
                      :class="{'text-primary': currentRoute.name.includes(pathItem.to?.name)}"
                      size="sm"
            />
            <AppListDivider v-else-if="pathItem.value === 'divider'" />
            <template
              v-if="pathItem.value === 'archives'"
            >
              <ArchiveAddAccessIcon
                :isArchivingEnabled="isArchivingEnabled"
                size="xs"
                :class="{'text-primary': isInArchiveRoute}"
              />
            </template>
            <span v-if="!pathItem.value !== 'divider'" class="font-weight-medium">
              {{ pathItem.text }}
            </span>
          </div>
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import {
  ARCHIVES_ROUTE_NAME,
  LOGOUT_ROUTE_NAME,
  PROFILE_ROUTE_NAME,
  SUBSCRIPTION_ROUTE_NAME,
} from '@/router/routerData'
import { getCurrentRouteSafe } from '@/router/routerInstance'
import { useArchivesStore } from '@/stores/archivesStore'

import AppListDivider from '../AppListDivider.vue'
import ArchiveAddAccessIcon from '../archives/ArchiveAddAccessIcon.vue'

export default {
  name: 'AppBarProfile',
  components: {
    ArchiveAddAccessIcon,
    AppListDivider,
  },
  setup() {
    const archivesStore = useArchivesStore()
    return { archivesStore }
  },
  props: {
    fullAccessScope: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      selectedPath: [],
    }
  },
  computed: {
    // We use this one because we are init even before the router is properly finished,
    // since the app can deduce the layout before then and we are at the top level
    currentRoute () {
      return getCurrentRouteSafe()
    },
    ...mapState('user', ['profile']),
    ...mapGetters('user', ['canCurrentUserSign', 'canAccessSubscription', 'isArchivingEnabled']),
    paths () {
      const paths = []
      const webinarUrl = import.meta.env.VUE_APP_WEBINAR_URL
      if (this.fullAccessScope) {
        paths.push({
          value: 'profile',
          text: this.$t('common.app-bar.ProfileMenuItem.profile'),
          icon: 'person',
          to: { name: PROFILE_ROUTE_NAME },
          locatorValue: 'profile-menu-item',
        })
        if (this.canAccessSubscription) {
          paths.push({
            value: 'subscription',
            text: this.$t('common.app-bar.ProfileMenuItem.subscription'),
            icon: 'credit_card',
            to: { name: SUBSCRIPTION_ROUTE_NAME },
            locatorValue: 'subscription-menu-item',
          })
        }
      }
      if (this.$vuetify.display.mobile) {
        paths.push({
          value: 'divider',
          customRender: true,
        })
        paths.push({
          value: 'help',
          text: this.$t('common.app-bar.ProfileMenuItem.helpCenter'),
          icon: 'help',
          href: `${import.meta.env.VUE_APP_KB_URL}/${this.$i18n.locale}`,
          locatorValue: 'help-menu-item',
        })
        if (this.$i18n.locale === 'fr' && webinarUrl) {
          paths.push({
            value: 'webinar',
            text: this.$t('common.app-bar.ProfileMenuItem.webinar'),
            icon: 'tv',
            href: webinarUrl,
            locatorValue: 'webinar-menu-item',
          })
        }
        if (this.fullAccessScope && this.profile.subscription && !this.profile.subscription?.hideArchiveOption) {
          // Component is directly in the template
          paths.push({
            value: 'archives',
            text: this.$t('common.app-bar.ProfileMenuItem.archives'),
            customRender: true,
            locatorValue: 'archives-menu-item',
            click: this.onArchivesClick,
          })
      }
      }
      paths.push({
        value: 'divider',
        customRender: true,
      })
      paths.push({
        value: 'logout',
        text: this.$t('common.app-bar.ProfileMenuItem.logout'),
        icon: 'power_settings_new',
        locatorValue: 'log-out-menu-item',
        to: {
          name: LOGOUT_ROUTE_NAME,
          query: {
            isVoluntaryLogOut: true,
            isLogOut: true,
          },
        },
      })
      return paths
    },
    isInArchiveRoute () {
      return this.currentRoute?.name?.includes(ARCHIVES_ROUTE_NAME)
    },
    isInProfileRoute () {
      return this.currentRoute?.name?.includes(PROFILE_ROUTE_NAME)
    },
    isInSubscriptionRoute () {
      return this.currentRoute?.name?.includes(SUBSCRIPTION_ROUTE_NAME)
    },
    isProfileLoaded () {
      return !!this.profile?.firstName && !!this.profile?.initials
    },
  },
  methods: {
    onArchivesClick() {
      if (this.isArchivingEnabled) {
        this.$router.push({
          name: ARCHIVES_ROUTE_NAME,
        })
      } else {
        this.archivesStore.setDiscoverElectronicSafeDialogIsOpen(true)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.profile-menu-item {
  height: 100%;

  &__button:hover {
    .profile-menu-item__initials {
      color: white;
      background: rgb(var(--v-theme-primary));
    }
  }

  &__initials {
    color: white;
    background: rgb(var(--v-theme-grey-900));
  }

  &--opened {
    .profile-menu-item__initials {
      color: white;
      background: rgb(var(--v-theme-primary));
    }
  }

  &__menu {
    font-size: 14px;
    font-weight: 500;

    :deep(.v-list-item--variant-text.v-list-item--active) {
      .v-list-item__overlay {
        background-color: rgb(var(--v-theme-primary));
      }
    }

    :deep(.v-list-subheader__text) {
      color: rgb(var(--v-theme-grey-800));
    }
  }
}
</style>
